export interface ListPriceControlsInput {
  /** The product's stored list price. Zero or absent means it has none. */
  listPrice?: number;
  /** Unit the stored list price is expressed in. */
  listPriceUnit?: string;
  /** Price currently entered in the form. */
  currentPrice?: number;
  /** Unit currently selected for that price. */
  currentUnit?: string;
}

export interface ListPriceControls {
  hasListPrice: boolean;
  isResetDisabled: boolean;
  canSaveNewListPrice: boolean;
  resetValues: { price: number; unit: string };
}

/**
 * Enabled/disabled state of the "Save as new list price" checkbox and its reset
 * button, from the price entered against the product's stored list price.
 *
 * A plain function rather than a hook: it reads no context and holds no state,
 * so each caller keeps ownership of how that state is stored.
 */
const deriveListPriceControls = ({
  listPrice = 0,
  listPriceUnit = '',
  currentPrice = 0,
  currentUnit = ''
}: ListPriceControlsInput): ListPriceControls => {
  const hasListPrice = listPrice > 0;
  const matchesListPrice =
    currentPrice === listPrice && currentUnit === listPriceUnit;

  return {
    hasListPrice,
    isResetDisabled: !hasListPrice || matchesListPrice,
    // Not the inverse of isResetDisabled: a product with no list price has
    // nothing to reset to, but its first price is still worth offering.
    canSaveNewListPrice:
      Number.isFinite(currentPrice) && currentPrice > 0 && !matchesListPrice,
    resetValues: { price: listPrice, unit: listPriceUnit }
  };
};

export default deriveListPriceControls;
